import { useCallback, useRef, useState } from 'react';
import { multiFactor, PhoneAuthProvider, PhoneMultiFactorGenerator, setPersistence, browserLocalPersistence, signInWithEmailAndPassword, sendEmailVerification, RecaptchaVerifier, GoogleAuthProvider, signInWithPopup, fetchSignInMethodsForEmail } from 'firebase/auth';
// logEvent also feeds the first-party stream via the GA4 bridge;
// the signature and GA4 behaviour are unchanged.
import { logEvent } from '../../analytics/ga4';
import { auth, analytics, db } from '../../firebase';
import { doc, getDoc, updateDoc, arrayUnion, serverTimestamp, deleteField, collection, query, where, getDocs } from 'firebase/firestore';
import { ensureGlobalRecaptchaContainer, createRecaptchaSlot, removeRecaptchaSlot } from './recaptchaUtils';
import { buildMfaResolver, sendMfaCode, determineMfaType, getTotpHint, buildTotpAssertion } from './mfaUtils';
import { isModularModule } from '../../constants/assessmentModules';

// Dev-only diagnostics toggle to avoid noisy logs in production
let hasLoggedAuthDiagnostics = false;

function translateAuthError(code) {
  switch (code) {
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-login-credentials':
    case 'auth/invalid-credential':
      return 'Incorrect credentials. Please try again.';
    case 'auth/too-many-requests':
      return 'Too many sign-in attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/invalid-app-credential':
      return 'An error occurred during sign in. Please try again.';
    case 'auth/user-disabled':
      // Intentionally returns the same generic message as wrong-password so
      // that an attacker cannot distinguish disabled from nonexistent accounts.
      // The distinct support code SI-004 (in mapSupportCode) allows internal
      // triage without leaking account status to the user.
      return 'Incorrect credentials. Please try again.';
    case 'auth/operation-not-allowed':
    case 'auth/internal-error':
      return 'An error occurred during sign in. Please try again.';
    default:
      return 'An error occurred during sign in. Please try again.';
  }
}

function normalizeFirebaseCode(e) {
  // Prefer explicit code if present
  if (e && typeof e === 'object' && e.code) return e.code;
  // Some errors only include the code in the message string
  const msg = typeof e === 'string' ? e : e?.message;
  if (typeof msg === 'string') {
    const match = msg.match(/auth\/[a-z-]+/i);
    if (match) return match[0];
  }
  return 'unknown';
}

// Support codes used in UI and analytics so users can share a code without
// revealing sensitive details. Simple meanings:
//
// EMAIL ENUMERATION PROTECTION NOTE
// ---------------------------------
// Firebase projects created after Sept 15, 2023 typically have Email Enumeration
// Protection enabled. With this protection:
// - Wrong email OR wrong password during sign‑in returns auth/invalid-credential
//   (or auth/invalid-login-credentials). The backend avoids returning
//   auth/user-not-found or auth/wrong-password for privacy.
// - fetchSignInMethodsForEmail() usually returns an empty array even for real
//   accounts, so client-side refinement cannot reliably distinguish email-missing
//   vs wrong-password.
//
// Design decision:
// - We generally map invalid sign‑ins to SI-001G (generic invalid credentials).
// - We only upgrade to SI-001P (wrong password) if we have a strong signal,
//   i.e. fetchSignInMethodsForEmail() returns a list that includes 'password'.
// - SI-001E (email not found) is still defined for completeness (and will be
//   used if the SDK emits auth/user-not-found or in legacy/non-protected envs),
//   but in this project with enumeration protection, we do not emit SI-001E from
//   the refinement step.
//
// SI-000: Unknown/uncategorized error
// SI-001P: Incorrect password (email exists but password wrong)
// SI-001E: Email not found (no account for this email)
// SI-001G: Generic invalid credentials (e.g. auth/invalid-credential)
// SI-002: Too many attempts (temporary lockout/throttled)
// SI-003: Network error (client offline/unstable connection)
// SI-004: Account disabled (contact admin)
// SI-005: Operation not allowed (provider-side configuration)
// SI-099: Reserved (not used)
// SI-100: MFA required (not an error; indicates next MFA step)
// SI-101: MFA setup error (failed to initiate sending code)
// SI-102: Invalid MFA code (wrong/expired code)
// SI-006: Invalid app credential (misconfigured reCAPTCHA/app verifier)
// SI-201: Resend throttled by client cooldown (<30s)
// SI-999: Internal error (provider-side)
function mapSupportCode(code) {
  switch (code) {
    case 'auth/wrong-password':
      return 'SI-001P'; // Incorrect password
    case 'auth/user-not-found':
      return 'SI-001E'; // Email not found
    case 'auth/invalid-login-credentials':
    case 'auth/invalid-credential':
      return 'SI-001G'; // Generic invalid credentials
    case 'auth/too-many-requests':
      return 'SI-002'; // Too many attempts / throttled
    case 'auth/network-request-failed':
      return 'SI-003'; // Network issues
    case 'auth/invalid-app-credential':
      return 'SI-006';
    case 'auth/user-disabled':
      return 'SI-004'; // Account disabled (contact admin)
    case 'auth/operation-not-allowed':
      return 'SI-005'; // Operation not allowed (provider-side configuration)
    case 'auth/internal-error':
      return 'SI-999'; // Internal error (provider-side)
    case 'auth/multi-factor-auth-required':
      return 'SI-100'; // MFA required (handled via flow)
    default:
      return 'SI-000'; // Unknown
  }
}

export default function useEmailPasswordSignIn({ entryAccountType, onRoute, recaptchaContainerId = 'recaptcha-container' }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  // mfaType: 'phone' | 'totp' | null — set after detecting which factor the user has enrolled.
  // totpHintUid: string | null — the uid of the TOTP hint from the resolver (needed for TOTP assertion).
  // For phone MFA: verificationId, maskedPhone, sentAt, resendCount, firstResendAt are used.
  // For TOTP MFA: only resolver and totpHintUid are needed (no reCAPTCHA, no SMS send).
  const [mfaState, setMfaState] = useState({ required: false, mfaType: null, verificationId: null, resolver: null, sentAt: 0, maskedPhone: '', totpHintUid: null, resendCount: 0, firstResendAt: 0 });
  const recaptchaVerifierRef = useRef(null);
  const lastSlotIdRef = useRef(null);
  // We intentionally avoid tracking a widget id; verifyPhoneNumber manages rendering/cleanup
  // for invisible reCAPTCHA. Keeping a widget id around invites manual renders and races.

  // Ensure the global base container exists (no-op if already present)
  ensureGlobalRecaptchaContainer();

  const resetRecaptcha = useCallback(() => {
    // Clear Firebase RecaptchaVerifier instance
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear();
      } catch (_) {}
      recaptchaVerifierRef.current = null;
    }
    // Do NOT clear the DOM container here. Clearing immediately after a successful
    // verify can race with Google's internal cleanup and cause a null style error.
    // We clear the container only at controlled points: right before creating a
    // new verifier or when the MFA flow is closed/completed.
  }, []);

  const startSignIn = useCallback(async (email, password) => {
    setError('');
    setErrorCode('');
    setIsLoading(true);
    // Entry surface only; authoritative accountType/userType come from claims after auth
    logEvent(analytics, 'login_start', { entryAccountType });
    // Normalize email to avoid false negatives in lookups (e.g., trailing spaces)
    const normalizedEmail = (email || '').trim().toLowerCase();
    // Development diagnostics to verify app/project/env alignment
    if (!hasLoggedAuthDiagnostics && (import.meta.env.VITE_ENVIRONMENT === 'development' || process.env.NODE_ENV === 'development')) {
      hasLoggedAuthDiagnostics = true;
      try {
        console.info('[AuthDiag] origin:', typeof window !== 'undefined' ? window.location?.origin : 'n/a');
        console.info('[AuthDiag] app.name:', auth?.app?.name);
        console.info('[AuthDiag] app.options projectId:', auth?.app?.options?.projectId);
        console.info('[AuthDiag] app.options apiKey len:', (auth?.app?.options?.apiKey || '').length);
        console.info('[AuthDiag] app.options authDomain:', auth?.app?.options?.authDomain);
        console.info('[AuthDiag] auth.languageCode:', auth?.languageCode);
        console.info('[AuthDiag] auth.tenantId:', auth?.tenantId || 'null');
        console.info('[AuthDiag] grecaptcha available:', typeof window !== 'undefined' && typeof window.grecaptcha !== 'undefined');
        console.info('[AuthDiag] grecaptcha.enterprise available:', typeof window !== 'undefined' && typeof window.grecaptcha?.enterprise !== 'undefined');
        console.info('[AuthDiag] #recaptcha-container exists:', typeof document !== 'undefined' ? !!document.getElementById('recaptcha-container') : 'n/a');
        console.info('[AuthDiag] entryAccountType:', entryAccountType);
        console.info('[AuthDiag] env:', { VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT, NODE_ENV: process.env.NODE_ENV });
      } catch (_e) {}
    }
    try {
      await setPersistence(auth, browserLocalPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, normalizedEmail, password);
      const user = userCredential.user;

      const idTokenResult = await user.getIdTokenResult(true);
      const claimedAccountType = idTokenResult?.claims?.accountType;
      const claimedUserType = idTokenResult?.claims?.userType;

      if (!user.emailVerified && (claimedAccountType === 'Provider' || claimedAccountType === 'RMS Provider User')) {
        await sendEmailVerification(user);
        logEvent(analytics, 'verification_email_sent', { accountType: claimedAccountType, userType: claimedUserType, entryAccountType });
        onRoute && onRoute('verifyEmail');
        return;
      }

      if (!idTokenResult.claims.hasSetOwnPassword) {
        logEvent(analytics, 'redirect_to_password_setup', { reason: 'hasSetOwnPassword_false', userId: user.uid, accountType: claimedAccountType, userType: claimedUserType, entryAccountType });
        onRoute && onRoute('passwordSetup', { email: normalizedEmail });
        return;
      }

      const multiFactorUser = multiFactor(user);
      const enrolledFactors = multiFactorUser.enrolledFactors;
      if (enrolledFactors.length === 0) {
        // Reconciliation: If MFA was removed via email link while the user was
        // signed out, Firestore may still indicate MFA is set up. To keep data
        // consistent, we clear stale flags on login and write an audit entry.
        //
        // Important UX note:
        // - Service users might land on the Provider sign-in page and still sign
        //   in successfully. We therefore MUST NOT depend on the entry screen's
        //   userKind to decide which collection to update. Instead, we derive the
        //   account type from the user's claims and reconcile the correct doc.
        // - If claims are missing/ambiguous, we attempt provider first, then
        //   service user, and only update an existing document with stale MFA.
        try {
          const claims = idTokenResult?.claims || {};
          const accountType = claims.accountType;
          const userType = claims.userType;

          // Helper: clear stale MFA flags from a given doc ref + snapshot
          const clearStaleMfa = async (ref, prev) => {
            await updateDoc(ref, {
              isMFASetUp: false,
              mfaRemovedAt: serverTimestamp(),
              mfaPhoneNumber: deleteField(),
              mfaMethod: deleteField(),
              mfaAuditTrail: arrayUnion({
                action: 'mfa_removed_detected_on_login',
                source: 'login_reconcile',
                at: new Date(),
                previous: {
                  isMFASetUp: prev.isMFASetUp ?? null,
                  mfaMethod: prev.mfaMethod ?? null,
                  mfaPhoneNumber: prev.mfaPhoneNumber ?? null,
                  mfaSetupAt: prev.mfaSetupAt ?? prev.mfaSetUpAt ?? null
                }
              })
            });
          };

          const reconcileClinician = async () => {
            const clinicianRef = doc(db, 'clinicians', user.uid);
            const snap = await getDoc(clinicianRef);
            if (snap.exists()) {
              const prev = snap.data() || {};
              if (prev.isMFASetUp || prev.mfaPhoneNumber || prev.mfaMethod) {
                await clearStaleMfa(clinicianRef, prev);
                return true;
              }
            }
            return false;
          };

          // RMS provider users live under rms_organisations/{rmsOrgId}/rmsProviderUsers
          // keyed by a prefixed UUID, not the Firebase UID. They carry the
          // dedicated `rmsOrgId` claim (post-#90), not the Assessment
          // `organisation` claim. We locate the doc by querying on accountId
          // and clear stale MFA flags if present (TD-31).
          const reconcileRmsProvider = async () => {
            const orgId = claims.rmsOrgId;
            if (!orgId) return false;
            const q = query(
              collection(db, 'rms_organisations', orgId, 'rmsProviderUsers'),
              where('accountId', '==', user.uid)
            );
            const snap = await getDocs(q);
            if (!snap.empty) {
              const providerDoc = snap.docs[0];
              const prev = providerDoc.data() || {};
              if (prev.isMFASetUp || prev.mfaPhoneNumber || prev.mfaMethod) {
                await clearStaleMfa(providerDoc.ref, prev);
                return true;
              }
            }
            return false;
          };

          const reconcilePatient = async () => {
            // Informants on modular pathways (Adult Autism, CYP ADHD, ...) live
            // in informants/ — go directly there based on claims to avoid a
            // wasted patients/ read. Keyed off the module registry so a new
            // modular pathway does not silently read the wrong collection.
            const isModularInformant =
              claimedUserType === 'Informant' &&
              isModularModule(idTokenResult?.claims?.assessmentModule);

            const profileRef = isModularInformant
              ? doc(db, 'informants', user.uid)
              : doc(db, 'patients', user.uid);
            const snap = await getDoc(profileRef);
            if (snap.exists()) {
              const prev = snap.data() || {};
              if (prev.isMFASetUp || prev.mfaPhoneNumber || prev.mfaMethod) {
                await clearStaleMfa(profileRef, prev);
                return true;
              }
            }
            return false;
          };

          if (accountType === 'RMS Provider User') {
            await reconcileRmsProvider();
          } else if (accountType === 'Provider') {
            await reconcileClinician();
          } else if (accountType === 'Service User' || userType === 'Patient' || userType === 'Informant') {
            await reconcilePatient();
          } else {
            // Fallback: try provider first (ADHD clinicians), then service user
            const didProvider = await reconcileClinician();
            if (!didProvider) {
              await reconcilePatient();
            }
          }
        } catch (_) {}
        logEvent(analytics, 'redirect_to_mfa_setup', { userId: user.uid, accountType: claimedAccountType, userType: claimedUserType, entryAccountType });
        onRoute && onRoute('mfaSetup');
        return;
      }

      // The bridge behind logEvent forwards login_success as
      // auth_login_succeeded; a direct emit here would queue the event a second
      // time under a different insert_id, which BigQuery cannot dedupe.
      logEvent(analytics, 'login_success', { method: 'email', userId: user.uid, accountType: claimedAccountType, userType: claimedUserType, entryAccountType });
      onRoute && onRoute('dashboard', { claims: idTokenResult.claims, user });
    } catch (e) {
      // Debug visibility during staging: ensure we see the raw error details
      console.error('useEmailPasswordSignIn:startSignIn error', e);
      const code = normalizeFirebaseCode(e);
      if (e.code === 'auth/multi-factor-auth-required') {
        try {
          // Verbose diagnostics to help identify where MFA setup fails
          console.info('[MFA] multi-factor-auth-required caught, building resolver');
          const resolver = buildMfaResolver(auth, e);
          console.info('[MFA] resolver built', { hints: resolver?.hints?.length || 0 });

          // Determine which MFA method to use based on enrolled factors.
          // TOTP is preferred because it does not require reCAPTCHA (critical for RPA bots).
          // Regular users only have phone/SMS enrolled, so they'll always get 'phone'.
          const mfaType = determineMfaType(resolver);
          console.info('[MFA] determined mfaType:', mfaType);

          // ── TOTP branch: no reCAPTCHA, no SMS sending ──
          // We just need to show the code input dialog. The user (or bot) enters
          // a 6-digit code generated from their authenticator app / TOTP library.
          if (mfaType === 'totp') {
            const totpHint = getTotpHint(resolver);
            console.info('[MFA] TOTP hint found, uid:', totpHint?.uid);
            setMfaState(prev => ({
              ...prev,
              required: true,
              mfaType: 'totp',
              resolver,
              totpHintUid: totpHint?.uid || null,
              // Clear phone-specific fields
              verificationId: null,
              maskedPhone: '',
              sentAt: 0,
            }));
            logEvent(analytics, 'mfa_required', { mfaType: 'totp', hintsCount: resolver.hints.length, entryAccountType });
            return;
          }

          // ── Phone/SMS branch: existing reCAPTCHA + SMS flow ──
          // If we recently sent a code (e.g., user dismissed dialog), reuse it (< 90s)
          if (mfaState.verificationId && Date.now() - mfaState.sentAt < 90_000) {
            setMfaState(prev => ({ ...prev, resolver, required: true, mfaType: 'phone' }));
            logEvent(analytics, 'mfa_required_reuse', { entryAccountType });
            return;
          }
          // If offline, avoid attempting SMS and surface a network error early
          if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            const offlineCode = 'auth/network-request-failed';
            const supportCode = mapSupportCode(offlineCode);
            setError(translateAuthError(offlineCode));
            setErrorCode(supportCode);
            console.warn('[MFA] offline detected prior to verifyPhoneNumber');
            return;
          }
          ensureGlobalRecaptchaContainer();
          resetRecaptcha();

          // Create RecaptchaVerifier in a unique slot under the global container to avoid
          // "already been rendered in this element" across re-logins.
          let slotId = null;
          try { slotId = createRecaptchaSlot(recaptchaContainerId); } catch (_) {}
          lastSlotIdRef.current = slotId;

          try {
            recaptchaVerifierRef.current = new RecaptchaVerifier(auth, slotId || recaptchaContainerId, {
              size: 'invisible',
              callback: (response) => console.info('[MFA] reCAPTCHA solved via callback, token:', (response || '').substring(0, 20) + '...'),
              'expired-callback': () => { console.warn('[MFA] reCAPTCHA expired'); }
            });
            console.info('[MFA] RecaptchaVerifier created (invisible)');
          } catch (verifierErr) {
            console.error('[MFA] Failed to create/render RecaptchaVerifier:', verifierErr);
            throw verifierErr;
          }
                    
          console.info('[MFA] Auth domain:', auth.config.authDomain);
          console.info('[MFA] Window origin:', window.location.origin);
          console.info('[MFA] calling verifyPhoneNumber');
          
          // Send code with helper — THIS IS WHERE CAPTCHA IS EVALUATED (phone MFA only)
          const { verificationId, maskedPhone } = await sendMfaCode(auth, resolver, recaptchaVerifierRef.current);
          console.info('[MFA] verifyPhoneNumber succeeded, verificationId obtained');
          // Defer cleanup of the verifier and slot to the next frame to avoid racing
          // with Google's internal post-solve styling/teardown.
          try {
            requestAnimationFrame(() => {
              setTimeout(() => {
                try { recaptchaVerifierRef.current?.clear(); } catch (_) {}
                recaptchaVerifierRef.current = null;
                try { removeRecaptchaSlot(lastSlotIdRef.current); } catch (_) {}
                lastSlotIdRef.current = null;
              }, 0);
            });
          } catch (_) {}
          setMfaState(prev => ({
            ...prev,
            required: true,
            mfaType: 'phone',
            verificationId,
            resolver,
            sentAt: Date.now(),
            maskedPhone,
            totpHintUid: null,
            resendCount: prev.resendCount || 0,
            firstResendAt: prev.firstResendAt || Date.now()
          }));
          logEvent(analytics, 'mfa_required', { mfaType: 'phone', hintsCount: resolver.hints.length, entryAccountType });
          return;
        } catch (mfaErr) {
          // Capture detailed error information for diagnostics while keeping UI message generic
          console.error('[MFA] setup error', { 
            code: mfaErr?.code, 
            message: mfaErr?.message,
            customData: mfaErr?.customData
          });
          
          // Try to extract more details from the Firebase error
          if (mfaErr?.customData) {
            console.error('[MFA] Firebase error details:', {
              appName: mfaErr.customData.appName,
              serverResponse: mfaErr.customData.serverResponse,
              _tokenResponse: mfaErr.customData._tokenResponse
            });
          }
          
          setError('Error setting up MFA verification. Please try again.');
          const code = normalizeFirebaseCode(mfaErr);
          const supportCode = mapSupportCode(code);
          setErrorCode(supportCode);
          setMfaState({ required: false, mfaType: null, verificationId: null, resolver: null, sentAt: 0, maskedPhone: '', totpHintUid: null, resendCount: 0, firstResendAt: 0 });
          return;
        }
      }
      setError(translateAuthError(code));
      let supportCode = mapSupportCode(code);
      // Refine ambiguous credential errors by checking whether the email exists.
      // IMPORTANT: With Email Enumeration Protection ON, this call normally
      // returns an empty array even for real accounts. Therefore, we only use
      // SI-001P when we explicitly see 'password' in methods. Otherwise we stick
      // with SI-001G to avoid leaking whether the email exists.
      if ((code === 'auth/invalid-credential' || code === 'auth/invalid-login-credentials') && normalizedEmail) {
        try {
          const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail);
          if (process.env.NODE_ENV === 'development') {
            console.info('[AuthDiag] fetchSignInMethodsForEmail:', normalizedEmail, methods);
          }
          if (methods && methods.includes('password')) {
            // Email exists and supports password sign-in -> likely wrong password
            supportCode = 'SI-001P';
          } else {
            // With email enumeration protection enabled, methods may be empty even for real accounts.
            // Avoid false "email not found"; prefer generic in that case.
            if (process.env.NODE_ENV === 'development') {
              console.info('[AuthDiag] Enumeration protection likely enabled; mapping to SI-001G (generic invalid credentials)');
            }
            supportCode = 'SI-001G';
          }
        } catch (_) {
          // If the refinement call fails, keep the generic mapping
        }
      }
      setErrorCode(supportCode);
      logEvent(analytics, 'login_error', { error: code, supportCode, entryAccountType, userId: auth.currentUser?.uid || 'unknown' });
    } finally {
      setIsLoading(false);
    }
  }, [onRoute, resetRecaptcha, entryAccountType, mfaState.sentAt, mfaState.verificationId, recaptchaContainerId]);

  // Google SSO sign-in that mirrors the email/password flow
  // Business rules for Provider accounts:
  // 1) If custom claim hasSetOwnPassword is false, we still require the user to set a password via SetUpPassword for recovery/compliance
  // 2) If MFA not enrolled, route to SetUpMFA
  // 3) Otherwise, continue to dashboard
  const startGoogleSignIn = useCallback(async (emailHint) => {
    setError('');
    setErrorCode('');
    setIsLoading(true);
    logEvent(analytics, 'login_start', { method: 'google', entryAccountType });
    try {
      await setPersistence(auth, browserLocalPersistence);

      // Configure Google provider. We optionally pass a login_hint so users with multiple Google accounts pick the intended one
      const provider = new GoogleAuthProvider();
      if (emailHint) {
        try { provider.setCustomParameters({ login_hint: emailHint }); } catch (_) {}
      }

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // For Google, email is already verified by the IdP. We still follow our normal gating rules below.
      const idTokenResult = await user.getIdTokenResult(true);
      const claimedAccountType = idTokenResult?.claims?.accountType;
      const claimedUserType = idTokenResult?.claims?.userType;

      // Enforce password setup for Provider users even if they arrive via Google SSO
      if (claimedAccountType === 'Provider' && !idTokenResult.claims.hasSetOwnPassword) {
        onRoute && onRoute('passwordSetup', { email: user.email });
        return;
      }

      // Enforce MFA for Provider users
      if (claimedAccountType === 'Provider') {
        const enrolled = multiFactor(user).enrolledFactors;
        if (!enrolled || enrolled.length === 0) {
          onRoute && onRoute('mfaSetup');
          return;
        }
      }

      // Single emitter, as in the email path above: the bridge behind logEvent
      // carries method and entry_account_type into auth_login_succeeded.
      logEvent(analytics, 'login_success', { method: 'google', userId: user.uid, accountType: claimedAccountType, userType: claimedUserType, entryAccountType });
      onRoute && onRoute('dashboard', { claims: idTokenResult.claims, user });
    } catch (e) {
      // MFA may still be required if account has MFA; handle the same way as password flow
      if (e?.code === 'auth/multi-factor-auth-required') {
        try {
          const resolver = buildMfaResolver(auth, e);
          const mfaType = determineMfaType(resolver);

          // ── TOTP branch (Google SSO): no reCAPTCHA needed ──
          if (mfaType === 'totp') {
            const totpHint = getTotpHint(resolver);
            setMfaState(prev => ({
              ...prev,
              required: true,
              mfaType: 'totp',
              resolver,
              totpHintUid: totpHint?.uid || null,
              verificationId: null,
              maskedPhone: '',
              sentAt: 0,
            }));
            logEvent(analytics, 'mfa_required', { mfaType: 'totp', hintsCount: resolver.hints.length, entryAccountType, method: 'google' });
            return;
          }

          // ── Phone/SMS branch (Google SSO): existing reCAPTCHA + SMS flow ──
          // If a recent code exists (< 90s), reuse and just show the dialog
          if (mfaState.verificationId && Date.now() - mfaState.sentAt < 90_000) {
            setMfaState(prev => ({ ...prev, resolver, required: true, mfaType: 'phone' }));
            logEvent(analytics, 'mfa_required_reuse', { entryAccountType, method: 'google' });
            return;
          }

          ensureGlobalRecaptchaContainer();
          resetRecaptcha();
          let slotId = null;
          try { slotId = createRecaptchaSlot(recaptchaContainerId); } catch (_) {}
          recaptchaVerifierRef.current = new RecaptchaVerifier(auth, slotId || recaptchaContainerId, {
            size: 'invisible',
            callback: () => {},
            'expired-callback': () => {}
          });
          const { verificationId, maskedPhone } = await sendMfaCode(auth, resolver, recaptchaVerifierRef.current);
          setMfaState(prev => ({
            ...prev,
            required: true,
            mfaType: 'phone',
            verificationId,
            resolver,
            sentAt: Date.now(),
            maskedPhone,
            totpHintUid: null,
            resendCount: prev.resendCount || 0,
            firstResendAt: prev.firstResendAt || Date.now()
          }));
          logEvent(analytics, 'mfa_required', { mfaType: 'phone', hintsCount: resolver.hints.length, entryAccountType, method: 'google' });
          return;
        } catch (mfaErr) {
          const code = normalizeFirebaseCode(mfaErr);
          const supportCode = mapSupportCode(code);
          setError('Error setting up MFA verification. Please try again.');
          setErrorCode(supportCode);
          setMfaState({ required: false, mfaType: null, verificationId: null, resolver: null, sentAt: 0, maskedPhone: '', totpHintUid: null, resendCount: 0, firstResendAt: 0 });
          return;
        }
      }

      // If the project is not configured for Google or the email is linked to another IdP without same-email linking
      const code = normalizeFirebaseCode(e);
      if (code === 'auth/account-exists-with-different-credential') {
        setError('This email is already registered with a different sign-in method. Please sign in with your password once, then you can use Google next time.');
      } else {
        setError(translateAuthError(code));
      }
      const supportCode = mapSupportCode(code);
      setErrorCode(supportCode);
      logEvent(analytics, 'login_error', { error: code, supportCode, entryAccountType, method: 'google' });
    } finally {
      setIsLoading(false);
    }
  }, [onRoute, recaptchaContainerId, resetRecaptcha, entryAccountType, mfaState.sentAt, mfaState.verificationId]);

  // completeMfa handles both phone/SMS and TOTP MFA verification.
  // It inspects mfaState.mfaType to determine which assertion to build:
  //   - 'phone': Uses verificationId + code → PhoneMultiFactorGenerator.assertion
  //   - 'totp':  Uses totpHintUid + code → TotpMultiFactorGenerator.assertionForSignIn
  // The caller (sign-in pages) doesn't need to know which type — they just pass the 6-digit code.
  const completeMfa = useCallback(async (verificationCode) => {
    if (!mfaState.required || !mfaState.resolver) return;

    // Guard: phone MFA needs a verificationId, TOTP needs a totpHintUid
    if (mfaState.mfaType === 'phone' && !mfaState.verificationId) return;
    if (mfaState.mfaType === 'totp' && !mfaState.totpHintUid) return;

    setError('');
    setErrorCode('');
    setIsLoading(true);
    try {
      let assertion;
      if (mfaState.mfaType === 'totp') {
        // TOTP assertion: no reCAPTCHA, no verificationId — just the hint uid + code
        assertion = buildTotpAssertion(mfaState.totpHintUid, verificationCode);
        console.info('[MFA] TOTP assertion built, resolving sign-in');
      } else {
        // Phone/SMS assertion: uses the verificationId from the SMS we sent earlier
        const cred = PhoneAuthProvider.credential(mfaState.verificationId, verificationCode);
        assertion = PhoneMultiFactorGenerator.assertion(cred);
      }

      await mfaState.resolver.resolveSignIn(assertion);
      const user = auth.currentUser;
      const idTokenResult = await user.getIdTokenResult(true);
      const claimedAccountType = idTokenResult?.claims?.accountType;
      const claimedUserType = idTokenResult?.claims?.userType;
      logEvent(analytics, 'mfa_complete', {
        mfaType: mfaState.mfaType || 'phone',
        userId: user?.uid || 'unknown',
        accountType: claimedAccountType,
        userType: claimedUserType,
        entryAccountType,
      });
      // On success, clear verifier and MFA UI state
      resetRecaptcha();
      setMfaState({ required: false, mfaType: null, verificationId: null, resolver: null, sentAt: 0, maskedPhone: '', totpHintUid: null, resendCount: 0, firstResendAt: 0 });
      onRoute && onRoute('dashboard', { claims: idTokenResult.claims, user });
    } catch (e) {
      setError('Invalid verification code. Please try again.');
      setErrorCode('SI-102');
      // Keep MFA state so user can retry with the correct code
    } finally {
      setIsLoading(false);
    }
  }, [mfaState, onRoute, resetRecaptcha, entryAccountType]);

  const cancelMfa = useCallback(() => {
    logEvent(analytics, 'mfa_cancelled', { mfaType: mfaState.mfaType || 'phone', entryAccountType });
    setMfaState({
      required: false,
      mfaType: mfaState.mfaType,
      verificationId: mfaState.verificationId,
      resolver: null,
      sentAt: mfaState.sentAt,
      maskedPhone: mfaState.maskedPhone,
      totpHintUid: mfaState.totpHintUid,
      resendCount: mfaState.resendCount,
      firstResendAt: mfaState.firstResendAt
    });
    resetRecaptcha();
    setError('');
    setErrorCode('');
  }, [mfaState.mfaType, mfaState.firstResendAt, mfaState.maskedPhone, mfaState.totpHintUid, mfaState.resendCount, mfaState.sentAt, mfaState.verificationId, resetRecaptcha, entryAccountType]);

  const resendMfa = useCallback(async () => {
    // TOTP codes are generated locally by the authenticator app — there's nothing
    // to "resend". If the user enters an expired code, they just need to wait for
    // the next 30-second window and enter the new code from their app.
    if (mfaState.mfaType === 'totp') {
      return { ok: false, supportCode: 'SI-000' };
    }

    const now = Date.now();
    const secondsSinceSent = (now - (mfaState.sentAt || 0)) / 1000;
    if (secondsSinceSent < 30) {
      const supportCode = 'SI-201';
      logEvent(analytics, 'mfa_resend', { throttled: true, supportCode, entryAccountType });
      setError('Please wait before requesting a new code.');
      setErrorCode(supportCode);
      return { ok: false, supportCode };
    }
    try {
      if (!mfaState.resolver) {
        const supportCode = 'SI-000';
        return { ok: false, supportCode };
      }
      resetRecaptcha();
      ensureGlobalRecaptchaContainer();
      // Create a new slot to avoid reusing the same DOM element across multiple resends
      let slotId = null;
      try { slotId = createRecaptchaSlot(recaptchaContainerId); } catch (_) {}
      lastSlotIdRef.current = slotId;
      // Create RecaptchaVerifier for resend bound to the new slot
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, slotId || recaptchaContainerId, {
        size: 'invisible',
        callback: () => console.info('[MFA] reCAPTCHA solved via callback (resend)'),
        'expired-callback': () => { console.warn('[MFA] reCAPTCHA expired (resend)'); }
      });
      
      const { verificationId, maskedPhone } = await sendMfaCode(auth, mfaState.resolver, recaptchaVerifierRef.current);
      setMfaState(prev => ({
        ...prev,
        required: true,
        verificationId,
        sentAt: now,
        maskedPhone,
        resendCount: (prev.resendCount || 0) + 1,
        firstResendAt: prev.firstResendAt || now
      }));
      // Defer cleanup similarly for resend path
      try {
        requestAnimationFrame(() => {
          setTimeout(() => {
            try { recaptchaVerifierRef.current?.clear(); } catch (_) {}
            recaptchaVerifierRef.current = null;
            try { removeRecaptchaSlot(lastSlotIdRef.current); } catch (_) {}
            lastSlotIdRef.current = null;
          }, 0);
        });
      } catch (_) {}
      logEvent(analytics, 'mfa_resend', { throttled: false, entryAccountType });
      setError('');
      setErrorCode('');
      return { ok: true };
    } catch (e) {
      const code = normalizeFirebaseCode(e);
      const supportCode = mapSupportCode(code);
      logEvent(analytics, 'mfa_resend', { throttled: code === 'auth/too-many-requests', supportCode, entryAccountType });
      setError(translateAuthError(code));
      setErrorCode(supportCode);
      return { ok: false, supportCode };
    }
  }, [mfaState.mfaType, mfaState.resolver, mfaState.sentAt, resetRecaptcha, entryAccountType, recaptchaContainerId]);

  return {
    isLoading,
    error,
    errorCode,
    mfaState,
    startSignIn,
    startGoogleSignIn,
    completeMfa,
    resetRecaptcha,
    cancelMfa,
    resendMfa
  };
}


